import { Columns } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Arrange, IconButton, Link, Typography } from "@aviary";
import { Container } from "@aviary/layouts";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { l } from "@emerson/locales/i18n";
import { SvgEmersonLogo } from "@shared/assets/logo";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import {
  type SiteLink,
  siteLinkGroup1,
  siteLinkGroup2,
  siteLinkGroup3,
  socialLinks,
} from "./siteLinks";

import * as styles from "./Footer.styles";

const Footer = () => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isMobile = tablet.lessThan;

  const renderSiteLinkGroup = (group: SiteLink[], bolded = false) => {
    return group.map(item => {
      return (
        <Link
          isColor="system"
          key={item.label}
          css={styles.footerLink(bolded)}
          href={item.link}
          noExternalIcon
        >
          {item.label}
        </Link>
      );
    });
  };

  const renderSocialMediaLinks = () => {
    return socialLinks.map(item => {
      return (
        <IconButton
          key={item.label}
          href={item.link}
          aria-label={item.label}
          isColor="system"
          isText
          size="xsmall"
        >
          <FontAwesomeIcon icon={item.icon} css={styles.iconButton} />
        </IconButton>
      );
    });
  };

  const renderResponsiveSocialMediaGroup = () => {
    return (
      <Arrange isVertical={!isMobile} justify="spaceEvenly">
        {renderSocialMediaLinks()}
      </Arrange>
    );
  };

  return (
    <footer css={styles.footerBase} data-testid="footer">
      <Container>
        <Columns>
          <Columns.Column columnWidth={7}>
            <Arrange
              isVertical
              isFullHeight
              justify="spaceEvenly"
              alignment={isMobile ? "center" : "start"}
            >
              <div>
                <Link to={emersonRoutes.base}>
                  <SvgEmersonLogo
                    css={styles.footerLogo}
                    role="img"
                    aria-label={t(l.header.Logo)}
                  />
                </Link>
              </div>
              {isMobile ? renderResponsiveSocialMediaGroup() : null}
              <Arrange>{renderSiteLinkGroup(siteLinkGroup3, true)}</Arrange>
              <Typography type="footnote">
                {t(l.footer.Copyright, { currentYear: new Date().getFullYear() })}
              </Typography>
            </Arrange>
          </Columns.Column>
          <Columns.Column columnWidth={1} phoneWidth={12} />
          <Columns.Column columnWidth={4} phoneWidth={5}>
            <Arrange justify={isMobile ? "spaceEvenly" : "spaceBetween"}>
              <Arrange isVertical>{renderSiteLinkGroup(siteLinkGroup1, true)}</Arrange>
              <Arrange isVertical>{renderSiteLinkGroup(siteLinkGroup2, true)}</Arrange>
              {isMobile ? null : renderResponsiveSocialMediaGroup()}
            </Arrange>
          </Columns.Column>
        </Columns>
        <hr />
        <Typography type="footnote">{t(l.aviary.footer.FDAdisclaimerLine1)}</Typography>
        <Typography type="footnote">{t(l.aviary.footer.FDAdisclaimerLine2)} </Typography>
      </Container>
    </footer>
  );
};
export { Footer };
